import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { AlertCircle, Save } from 'react-feather';
import config from '../../../../config';
import ModalShell from '../shared/ModalShell';

/**
 * Add / Edit modal for the Subcategory entity. Requires a parent category.
 */
const SubcategoryFormModal = ({ mode = 'add', entityId, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [categories, setCategories] = useState([]);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const concessionaireId = localStorage.getItem('concessionaireId');
    const loginId = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = { Authorization: `${token}` };

    // Load categories for the dropdown, and (if editing) the subcategory.
    useEffect(() => {
        let cancelled = false;
        const fetches = [
            axios.post(
                `${config.hostname}/category/getcategorybyconcessionaire`,
                { category_concessionaire_id: concessionaireId },
                { headers },
            ),
        ];
        if (mode === 'edit' && entityId) {
            fetches.push(axios.get(`${config.hostname}/subcategory/${entityId}`, { headers }));
        }
        Promise.all(fetches)
            .then(([catRes, subRes]) => {
                if (cancelled) return;
                setCategories(Array.isArray(catRes.data) ? catRes.data : []);
                if (subRes) {
                    setName(subRes.data.subcategory_name || '');
                    const cid = subRes.data.category_id?._id || subRes.data.category_id || '';
                    setCategoryId(cid);
                }
            })
            .catch((err) => {
                console.error(err);
                toast.error(t('Failed to load form'));
                onClose?.();
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [mode, entityId]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const next = {};
        if (!name.trim()) next.name = t('Subcategory name is required');
        if (!categoryId) next.category = t('Select a parent category');
        setErrors(next);
        if (Object.keys(next).length) return;

        setSaving(true);
        const payload = {
            subcategory_name: name,
            category_id: categoryId,
            subcategory_concessionaire_id: concessionaireId,
            login_id: loginId,
        };
        const req = mode === 'add'
            ? axios.post(`${config.hostname}/subcategory`, payload, { headers })
            : axios.post(`${config.hostname}/subcategory/updatesubcategory/${entityId}`, payload, { headers });
        req
            .then(() => {
                toast.success(mode === 'add' ? t('Subcategory added') : t('Subcategory updated'));
                onSaved?.();
                onClose?.();
            })
            .catch((err) => {
                console.error(err);
                toast.error(t('Save failed'));
            })
            .finally(() => setSaving(false));
    };

    return (
        <ModalShell title={mode === 'add' ? t('Add subcategory') : t('Edit subcategory')} onClose={onClose}>
            {loading ? (
                <div className="v3-sadmin-empty"><p>{t('Loading…')}</p></div>
            ) : (
                <form className="v3-sadmin-form" onSubmit={handleSubmit}>
                    <div className="v3-sadmin-form-grid">
                        <label className="v3-field">
                            <span className="v3-label">{t('Parent category')}</span>
                            <div className="v3-select-wrap">
                                <select
                                    className="v3-input v3-select"
                                    value={categoryId}
                                    onChange={(e) => setCategoryId(e.target.value)}
                                >
                                    <option value="">{t('Select category…')}</option>
                                    {categories.map((c) => (
                                        <option key={c._id} value={c._id}>{c.category_name}</option>
                                    ))}
                                </select>
                            </div>
                            {errors.category && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.category}</span>
                            )}
                        </label>
                        <label className="v3-field">
                            <span className="v3-label">{t('Subcategory name')}</span>
                            <input
                                className="v3-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={t("Men's wear, Coffee, Mobile…")}
                            />
                            {errors.name && (
                                <span className="v3-sadmin-field-err"><AlertCircle size={12} />{errors.name}</span>
                            )}
                        </label>
                    </div>
                    <div className="v3-sadmin-form-actions">
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={onClose} disabled={saving}>
                            {t('Cancel')}
                        </button>
                        <button type="submit" className="v3-btn v3-btn-primary" disabled={saving}>
                            <Save size={14} /> {saving ? t('Saving…') : mode === 'add' ? t('Add subcategory') : t('Save changes')}
                        </button>
                    </div>
                </form>
            )}
        </ModalShell>
    );
};

export default SubcategoryFormModal;
